import styled, { css } from 'styled-components';
import type { RefObject } from 'react';
import { Text } from '@lmn/design-system';
import { FeatureType } from '@/app/components/search/components/Card/utils/facilitiesTypes.ts';
import type { Localization } from '../../../../../common/Localization';
import { OffersContainer } from '../styled';
import { IconWithTooltip } from '../../../../Tooltip/styled';

const specificStyles = css`
  &.loader {
    margin-top: 4px;
    padding: 0;

    &:first-child {
      margin-top: 0;
    }

    &.flight {
      &:first-child {
        > div {
          width: 266px;
        }
      }
    }
  }

  &.loader.date {
    width: 100%;
    margin: 12px 0 16px 0;
  }

  &.direct {
    @media (min-width: 768px) {
      padding: 8px 0 0;
      border-top: 1px solid ${({ theme }) => theme.kallisto.color.common.black[100]};
      margin-top: auto;
      width: 100%;
    }
  }

  &:has(+ .direct) {
    @media (min-width: 768px) {
      // force  margin on the element before direct USP
      margin-bottom: 8px;
    }
  }

  &.alternative-dates {
    display: flex;
    margin-bottom: 8px;
  }

  &.dates-more {
    width: 100%;

    .text {
      display: flex;
      flex-wrap: nowrap;
      width: 100%;
      justify-content: space-between;
      align-items: center;
    }

    .more {
      flex-shrink: 0;
      color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
      text-align: right;
    }
  }

  &.voucher {
    .icon-right {
      opacity: 0.5;
    }
  }
`;

const featureTypeCustomStyles = (featureType: FeatureType) => {
  switch (featureType) {
    case FeatureType.normal:
      return css``;
    case FeatureType.gray:
      return css`
        color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
      `;
    case FeatureType.dates:
      return css`
        .text {
          font-size: ${({ theme }) => theme.kallisto.font.size['175']};
          font-weight: ${({ theme }) => theme.kallisto.font.weight.bold};
          line-height: ${({ theme }) => theme.kallisto.font.lineHeight['250']};

          .more {
            font-weight: ${({ theme }) => theme.kallisto.font.weight.regular};
          }
        }
      `;
    case FeatureType.bold:
      return css`
        .text {
          font-weight: ${({ theme }) => theme.kallisto.font.weight.bold};
        }
      `;
    case FeatureType.primary:
      return css`
        color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};

        * {
          color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
        }
      `;
    case FeatureType.danger:
      return css`
        margin: 0 0 12px;
        .text {
          color: ${({ theme }) => theme.kallisto.color.common.semantic.error[400]} !important;
          font-size: ${({ theme }) => theme.kallisto.font.size['175']};
          font-weight: ${({ theme }) => theme.kallisto.font.weight.bold};
          line-height: ${({ theme }) => theme.kallisto.font.lineHeight['250']};
        }
      `;
    case FeatureType.offerGray:
      return css`
        background-color: ${({ theme }) => theme.kallisto.color.common.black[50]};
        color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
        font-style: normal;
        font-weight: ${({ theme }) => theme.kallisto.font.weight.regular};
        font-size: ${({ theme }) => theme.kallisto.font.size['175']};
        line-height: ${({ theme }) => theme.kallisto.font.lineHeight['250']};
        padding: 4px 8px;
        border-radius: 4px;
        margin-left: 0;
        margin-bottom: 4px;
        width: auto;
      `;
    case FeatureType.offer:
      return css`
        background-color: ${({ theme }) => theme.kallisto.color.background.interactive.backgroundPrimarySelected};
        color: ${({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary};
        font-style: normal;
        font-weight: ${({ theme }) => theme.kallisto.font.weight.regular};
        font-size: ${({ theme }) => theme.kallisto.font.size['175']};
        line-height: ${({ theme }) => theme.kallisto.font.lineHeight['250']};
        padding: 4px 8px;
        border-radius: 4px;
        margin-left: 0;
        margin-bottom: 4px;
        width: auto;
      `;
    case FeatureType.link:
      return css`
        .text img {
          margin-right: 4px;
        }

        .text {
          text-decoration: underline;
        }
      `;
    default:
      return css``;
  }
};

export const FeatureIcon = styled.div`
  display: flex;
  align-items: center;
  height: 20px;
  /* A margin here, not on the icon: the icon's own margin holds its footprint. */
  margin-right: 4px;

  .group-luggage & img {
    margin-top: 2px;
  }
`;

export const FeatureText = styled(Text)`
  .group-luggage & .icon-right {
    margin: 0;
  }

  .group-luggage & {
    display: flex;
    gap: 4px;
    align-items: center;
  }

  .group-luggage & span div {
    margin: 0;
  }

  .group-luggage & span {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .group-luggage-bold & span {
    font-weight: 600;
  }

  .dates-more & {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .package-discount-usp & {
    b {
      margin-right: 4px;
    }
  }

  .icon-right {
    margin-left: 4px;
  }

  .text {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
  }
`;

export const FeatureContainer = styled(Text)<{
  featureType: FeatureType;
  isLast?: boolean;
  icon?: string;
  localization: Localization;
  isInteractive: boolean;
}>`
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  display: inline-flex;
  flex-direction: row;
  position: relative;
  padding: 2px 0;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  font-family: inherit;

  ${({ isInteractive }) =>
    !isInteractive &&
    css`
      cursor: inherit;
    `}

  &:disabled {
    cursor: default;
  }

  ${IconWithTooltip} {
    height: ${({ theme }) => theme.kallisto.font.lineHeight['250']};

    display: inline-flex;
  }

  ${IconWithTooltip} > img {
    width: 16px;
    height: 16px;
    margin-left: 4px;
  }

  ${OffersContainer} & {
    margin-right: 4px;
    margin-top: 0;

    &:only-child {
      margin-right: 0;
    }
  }

  ${({ featureType }) => featureType && featureTypeCustomStyles(featureType)};
  ${specificStyles};
`;

export const FeatureLink = styled.button<{ ref?: RefObject<HTMLButtonElement | null> }>`
  line-height: ${({ theme }) => theme.kallisto.font.lineHeight['300']};
  display: flex;
  flex-direction: row;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  color: inherit;
  text-align: left;
`;
